import { memo, useCallback, useMemo } from 'react';

import { Box } from '@/shared/ui';

import type { CatalogItem } from '@/entities/catalog-item';
import type { SymbolKey } from '@/entities/symbol';
import { isSameSymbol, isSameSymbolIgnoringPeriod } from '@/entities/symbol';
import FactsheetDialogButton from '@/features/Factsheet/components/FactsheetDialogButton';
import { requestForwardCurveSelection, useSelectionFunctions } from '@/features/symbol-selector';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import IndeterminateCheckbox from '@/shared/ui/primitives/Checkbox/IndeterminateCheckbox';
import useCatalogTableCell from '../useCatalogTableCell';

import type { Cell } from '@tanstack/react-table';

const factsheetCellSx = { display: 'flex', justifyContent: 'center', width: '100%' };

const ForwardCatalogCell = ({
  cell,
  symbols,
  isRestrictedData,
  isInDashboard
}: {
  cell: Cell<CatalogItem, unknown>;
  symbols: SymbolKey[];
  isRestrictedData: boolean;
  isInDashboard: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { isSpotLocked, isForwardCurveLocked } = useSelectionFunctions();
  const selectedSymbols = useAppSelector(state => state.symbolSelector.symbols);
  const columnId = cell.column.id;
  const cellValue = cell.getValue() as string;
  const { renderContent, defaultStyle } = useCatalogTableCell(columnId, cell);

  const { firstSelected, secondSelected } = useMemo(() => {
    const equals = isSpotLocked ? isSameSymbolIgnoringPeriod : isSameSymbol;
    if (!symbols.length) return { firstSelected: false, secondSelected: false };
    let any = false;
    let all = true;
    for (const sym of symbols) {
      let found = false;
      for (const s of selectedSymbols) {
        if (equals(s, sym)) {
          found = true;
          break;
        }
      }
      if (found) any = true;
      else all = false;
    }
    return { firstSelected: any, secondSelected: all };
  }, [selectedSymbols, symbols, isSpotLocked]);

  const onChange = useCallback(() => {
    if (!symbols.length) return;
    dispatch(requestForwardCurveSelection({ symbols }));
  }, [dispatch, symbols]);

  const renderCellContent = () => {
    switch (columnId) {
      case 'Symbol': {
        return (
          <IndeterminateCheckbox
            disabled={isRestrictedData}
            firstSelected={firstSelected}
            secondSelected={secondSelected}
            modeLocked={isForwardCurveLocked}
            onLockedClick={onChange}
            onChange={onChange}
          />
        );
      }
      case 'Index':
        return <span>{cellValue === 'Y' ? 'Index' : 'High, Mid, Low'}</span>;
      case 'Periods':
        return <span>{cellValue.toString().padStart(2, '0')}</span>;
      case 'Factsheet':
        return (
          <Box sx={factsheetCellSx}>
            <FactsheetDialogButton
              code={cell.row.getValue('Code')}
              mode={isInDashboard ? 'newTab' : 'modal'}
            />
          </Box>
        );
      default:
        return <>{renderContent()}</>;
    }
  };

  return (
    <div
      key={cell.id + columnId}
      style={defaultStyle}
    >
      {renderCellContent()}
    </div>
  );
};

export default memo(ForwardCatalogCell);
